import { useEffect, useMemo, useRef, useState } from 'react'
import { isMacOs } from 'react-device-detect'
import { useEditor } from '@tiptap/react'

import LockOutlinedIcon from '@ant-design/icons/LockOutlined'
import SendOutlinedIcon from '@ant-design/icons/SendOutlined'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'
import FormHelperText from '@mui/material/FormHelperText'
import Stack from '@mui/material/Stack'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import useDraft from '@shared/hooks/src/useDraft'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { clipboardTextParser, richTextStyles } from '@shared/messaging/src/RichTextHelper'
import { useMe } from '@shared/providers/src/MeProvider'
import { processRichTextJsonToMessage, UserSettingName } from '@shared/utils'

import { outlineRichTextEditorStyling } from '@utils/StylesHelper'
import { getExtensions, RichTextEditor } from '@components/RichText'
import KeyboardHandlerExtension from '@components/RichText/KeyboardHandlerExtension'

import { hasMention, MENTION_REQUIRED_MESSAGE, ReplyTo, SendOnBehalfSelector } from './ThreadMessageComposer.utils'

/**
 * A component for composing and sending thread messages with rich text editor support.
 *
 * @param {Object} props - The properties required to render the ThreadMessageComposer.
 * @param {boolean} [props.mini=false] - Whether the editor should be displayed in a compact, smaller form.
 * @param {string} [props.threadId='new'] - The unique identifier for the thread being composed.
 * @param {Array} [props.users=[]] - An array of user objects to allow message-sending on behalf of others.
 * @param {Object} [props.replyMessage] - The message object being replied to.
 * @param {Function} [props.onReplyCancel] - A callback function fired when replying is canceled.
 * @param {string} [props.unavailable] - A message displayed when the component is disabled or unavailable.
 * @param {boolean} [props.disabled=false] - Whether the send button should be disabled.
 * @param {boolean} [props.requireMention=false] - Whether the message must contain at least one mention to be sent.
 * @param {Function} props.onSend - A function to call with the message data when sending is triggered.
 * @param {Array} [props.extensions=[]] - Additional extensions to enhance the editor's functionality.
 *
 * @return {JSX.Element} The rendered component for thread message composition.
 */
export default function ThreadMessageComposer({
  mini = false,
  threadId = 'new',
  users = [],
  replyMessage,
  onReplyCancel,
  unavailable,
  disabled,
  requireMention = false,
  onSend,
  extensions = [],
}) {
  const handleSendRef = useRef()

  const me = useMe()
  const { draft, setDraft, removeDraft } = useDraft(`thread-${threadId}`)
  const sendOnEnter = useUserSetting(me, UserSettingName.SendOnEnter)?.value ?? true

  const [isOnBehalf, setIsOnBehalf] = useState(false)
  const [onBehalfOf, setOnBehalfOf] = useState('')
  const [mentionMissing, setMentionMissing] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      // Should be before mention extension
      KeyboardHandlerExtension.configure({
        sendOnEnter,
        onSend: () => handleSendRef.current(),
      }),
      ...getExtensions({ enableQPhrases: true }),
      ...extensions,
    ],
    content: draft,
    autofocus: 'end',
    editorProps: { clipboardTextParser },
    onUpdate: ({ editor }) => (editor.isEmpty ? removeDraft() : setDraft(editor.getJSON())),
  })

  const mentionsEnabled = extensions.some((ext) => ext.name === 'mention')
  const mentionRequired = requireMention && !replyMessage
  const showMentionError = mentionMissing && mentionRequired && !hasMention(editor)

  const handleSend = async () => {
    if (isSending) return

    if (mentionRequired && !hasMention(editor)) return setMentionMissing(true)

    const message = editor.getJSON()

    setMentionMissing(false)
    setIsSending(true)

    try {
      await onSend({
        message: processRichTextJsonToMessage(message),
        fromUserId: isOnBehalf ? onBehalfOf : undefined,
        replyTo: replyMessage?.id,
      })
    } catch {
      return
    } finally {
      setIsSending(false)
    }

    onReplyCancel?.()
    if (!editor.isDestroyed) editor.commands.clearContent()
    removeDraft()
  }

  handleSendRef.current = handleSend

  useEffect(() => {
    // `editor.isDestroyed` is true whenever the ProseMirror view isn't mounted, so this
    // skips `focus()` during the unmount/navigation race that otherwise throws
    // "[tiptap error]: The editor view is not available" (Minified React error #520).
    if (replyMessage && editor && !editor.isDestroyed) {
      editor.commands.focus()
    }
  }, [editor, replyMessage])

  const placeholder = useMemo(() => {
    const enterText = isMacOs ? 'Return' : 'Enter'
    const mentionText = mentionsEnabled ? "Use '@' to mention a user. " : ''

    return sendOnEnter
      ? `${mentionText}Press '${enterText}' to send, 'Shift + ${enterText}' to add a new line`
      : `${mentionText}Press '${enterText}' to add a new line`
  }, [mentionsEnabled, sendOnEnter])

  if (unavailable) {
    return (
      <Fade in>
        <Typography sx={{ color: 'text.secondary', bgcolor: 'grey.200', px: 3, py: 2 }}>
          <LockOutlinedIcon style={{ marginRight: '8px' }} />
          {unavailable}
        </Typography>
      </Fade>
    )
  }

  const btnDisabled = editor.isEmpty || disabled || isSending

  return (
    <Stack>
      <Collapse in={Boolean(replyMessage)} unmountOnExit>
        <ReplyTo message={replyMessage} onCancel={onReplyCancel} />
      </Collapse>
      <Collapse in={users.length > 0} unmountOnExit>
        <SendOnBehalfSelector
          users={users}
          isOnBehalf={isOnBehalf}
          setIsOnBehalf={setIsOnBehalf}
          onBehalfOf={onBehalfOf}
          setOnBehalfOf={setOnBehalfOf}
        />
      </Collapse>
      <Stack spacing={1} sx={outlineRichTextEditorStyling}>
        <Box sx={[richTextStyles, { '& .tiptap': { maxHeight: mini ? 100 : 300 } }]}>
          <RichTextEditor editor={editor} />
        </Box>
        <Stack direction="row" sx={{ alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <FormHelperText error={showMentionError}>{showMentionError ? MENTION_REQUIRED_MESSAGE : placeholder}</FormHelperText>
          <IconButton color="primary" size="small" variant={btnDisabled ? 'text' : 'contained'} disabled={btnDisabled} onClick={handleSend}>
            <SendOutlinedIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  )
}
